import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Button,
  Form,
  FormProps,
  Input,
  message,
  Radio,
  Tabs,
  Tooltip,
} from "antd";
import {
  currencyFormItems,
  getCollateralPropertyAddressFormItems,
  quantityFormItems,
  rentedPropertyAddressFormItem,
  yesNoFormItems,
} from "./fixtures/analysisForm.ts";
import {
  CheckOutlined,
  SaveFilled,
  ThunderboltFilled,
} from "@ant-design/icons";
import { useAppDispatch, useAppSelector } from "../../redux/hooks.ts";
import {
  fetchFolderById,
  onUpdateAnalysisForm,
} from "../../redux/actions/folderActions.tsx";
import { Analysis } from "../../redux/interfaces/folder.types.ts";
import { onSetAddressFields } from "../../redux/actions/requirementsActions.tsx";
import {
  getAnalysisFormProgress,
  saveAnalysisFormProgress,
} from "../../utils/folders/analysisFormProgress.ts";
import { hasCollateral } from "../../utils/folders/hasCollateral.ts";
import { extractErrorMessage } from "../../utils/extractErrorMessage.ts";
import {
  AnalysisDerivations,
  deriveAnalysisDefaults,
} from "../../utils/folders/deriveAnalysisDefaults.ts";

type SectionStatus = {
  dirty: boolean;
  savedAt: Date | null;
};

const formatTimeAgo = (date: Date, nowMs: number): string => {
  const seconds = Math.max(0, Math.floor((nowMs - date.getTime()) / 1000));
  if (seconds < 5) return "hace un momento";
  if (seconds < 60) return `hace ${seconds}s`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `hace ${minutes} min`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `hace ${hours} h`;
  const days = Math.floor(hours / 24);
  return `hace ${days} d`;
};

const StatusChip: React.FC<{ status: SectionStatus; nowMs: number }> = ({
  status,
  nowMs,
}) => {
  if (status.dirty) {
    return (
      <span className="inline-flex items-center gap-1.5 text-xs font-medium text-amber-700 bg-amber-50 border border-amber-200 px-2.5 py-1 rounded-full">
        <span className="w-1.5 h-1.5 rounded-full bg-amber-500" />
        Cambios sin guardar
      </span>
    );
  }
  if (status.savedAt) {
    return (
      <span className="inline-flex items-center gap-1.5 text-xs font-medium text-emerald-700 bg-emerald-50 border border-emerald-200 px-2.5 py-1 rounded-full">
        <CheckOutlined style={{ fontSize: 10 }} />
        Guardado {formatTimeAgo(status.savedAt, nowMs)}
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1.5 text-xs font-medium text-slate-500 bg-slate-50 border border-slate-200 px-2.5 py-1 rounded-full">
      Sin cambios
    </span>
  );
};

const TabLabel: React.FC<{ title: string; status: SectionStatus }> = ({
  title,
  status,
}) => {
  let dotClass = "";
  if (status.dirty) dotClass = "bg-amber-500";
  else if (status.savedAt) dotClass = "bg-emerald-500";

  return (
    <span className="inline-flex items-center gap-2">
      {title}
      {dotClass && <span className={`w-1.5 h-1.5 rounded-full ${dotClass}`} />}
    </span>
  );
};

export const AnalysisForm: React.FC = () => {
  const [analysisFormData] = Form.useForm();
  const [propertyFormData] = Form.useForm();
  const [rentedPropertyFormData] = Form.useForm();
  const [messageApi, contextHolder] = message.useMessage();

  const dispatch = useAppDispatch();
  const { activeFolder, loadingAnalysis } = useAppSelector(
    (state) => state.folder,
  );
  const analysisForm: Analysis | null = activeFolder?.analysis || null;

  const hasCollateralSubfolder = hasCollateral(activeFolder!);
  const isNewRegistryReceipt = Form.useWatch(
    "is_new_boleta_registral",
    propertyFormData,
  );
  const accreditationType:
    | "boleta_registral"
    | "predial"
    | "escrituras"
    | undefined = Form.useWatch(
    "guarantee_accreditation_type",
    propertyFormData,
  );

  const [analysisStatus, setAnalysisStatus] = useState<SectionStatus>({
    dirty: false,
    savedAt: null,
  });
  const [rentedStatus, setRentedStatus] = useState<SectionStatus>({
    dirty: false,
    savedAt: null,
  });
  const [propertyStatus, setPropertyStatus] = useState<SectionStatus>({
    dirty: false,
    savedAt: null,
  });
  const [nowMs, setNowMs] = useState(Date.now());
  const [activeTab, setActiveTab] = useState<string>("analysis");
  const refetchedFolderIdRef = useRef<string | null>(null);

  const derivations: AnalysisDerivations = useMemo(
    () => (activeFolder ? deriveAnalysisDefaults(activeFolder) : {}),
    [activeFolder],
  );

  const watchedAnalysisValues = Form.useWatch([], analysisFormData) as
    | Record<string, any>
    | undefined;

  useEffect(() => {
    const id = setInterval(() => setNowMs(Date.now()), 15000);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    if (
      activeFolder?.id &&
      refetchedFolderIdRef.current !== activeFolder.id
    ) {
      refetchedFolderIdRef.current = activeFolder.id;
      dispatch(fetchFolderById(activeFolder.id));
    }
  }, [activeFolder?.id, dispatch]);

  const initializeAnalysisForm = () => {
    // Each section's form is only repopulated when it has no unsaved edits.
    // Otherwise saving one tab (which triggers an activeFolder refresh →
    // re-init) would silently overwrite drafts in the other tabs.
    const canResetAnalysis = !analysisStatus.dirty;
    const canResetRented = !rentedStatus.dirty;
    const currentPropertyValues = propertyFormData.getFieldsValue();
    const hasPropertyData = Object.keys(currentPropertyValues).some(
      (key) =>
        currentPropertyValues[key] !== undefined &&
        currentPropertyValues[key] !== null &&
        currentPropertyValues[key] !== "",
    );
    const canResetProperty = !propertyStatus.dirty && !hasPropertyData;

    if (!analysisForm) {
      const progress = getAnalysisFormProgress(activeFolder!.id);
      if (canResetAnalysis) {
        analysisFormData.setFieldsValue({ ...progress });
      }
      if (canResetRented) {
        rentedPropertyFormData.setFieldsValue({
          rented_property_address:
            (progress as any).rented_property_address ?? "",
        });
      }
      if (canResetProperty) {
        propertyFormData.setFieldsValue({
          ...progress,
          is_new_boleta_registral:
            (progress as any).is_new_boleta_registral ?? true,
        });
      }
    } else {
      if (canResetAnalysis) {
        analysisFormData.setFieldsValue({ ...analysisForm });
      }
      if (canResetRented) {
        rentedPropertyFormData.setFieldsValue({
          rented_property_address: analysisForm.rented_property_address ?? "",
        });
      }
      if (canResetProperty) {
        propertyFormData.setFieldsValue({
          ...analysisForm,
          is_new_boleta_registral: analysisForm.is_new_boleta_registral ?? true,
        });
      }
    }

    // Apply derivations whenever the current value differs from what we'd
    // derive. Skip when the analysis section is dirty — overwriting the
    // analyst's in-progress edits would surprise them.
    if (!canResetAnalysis) return;
    const currentAnalysisValues = analysisFormData.getFieldsValue();
    const fillsToApply: Record<string, boolean> = {};
    for (const [field, derivation] of Object.entries(derivations)) {
      if (!derivation) continue;
      const current = currentAnalysisValues[field];
      if (current !== derivation.value) {
        fillsToApply[field] = derivation.value;
      }
    }
    if (Object.keys(fillsToApply).length > 0) {
      analysisFormData.setFieldsValue(fillsToApply);
      // Auto-fills are not persisted to the backend until the analyst clicks
      // "Guardar". Mark the section dirty so they see "Cambios sin guardar"
      // and the Guardar button enables immediately.
      setAnalysisStatus((s) => ({ ...s, dirty: true }));
      handleSaveCurrentProgress();
    }
  };

  const handleSaveCurrentProgress = () => {
    const folderId = activeFolder!.id;
    const analysisValues = analysisFormData.getFieldsValue();
    const propertyValues = propertyFormData.getFieldsValue();
    const rentedPropertyValues = rentedPropertyFormData.getFieldsValue();

    saveAnalysisFormProgress(folderId, {
      ...analysisValues,
      ...propertyValues,
      ...rentedPropertyValues,
    });
  };

  const handleSaveAnalysisForm = async () => {
    try {
      await analysisFormData.validateFields();
      analysisFormData.submit();
    } catch {
      handleSaveCurrentProgress();
    }
  };

  const handleSaveGuaranteePropertyForm = async () => {
    try {
      await propertyFormData.validateFields();
      propertyFormData.submit();
    } catch {
      handleSaveCurrentProgress();
    }
  };

  const handleSaveRentedPropertyForm = async () => {
    try {
      await rentedPropertyFormData.validateFields();
      rentedPropertyFormData.submit();
    } catch {
      handleSaveCurrentProgress();
    }
  };

  const handleDiscardAnalysis = () => {
    analysisFormData.resetFields();
    if (analysisForm) {
      analysisFormData.setFieldsValue({ ...analysisForm });
    }
    setAnalysisStatus((s) => ({ ...s, dirty: false }));
  };

  const handleDiscardRented = () => {
    rentedPropertyFormData.resetFields();
    rentedPropertyFormData.setFieldsValue({
      rented_property_address: analysisForm?.rented_property_address ?? "",
    });
    setRentedStatus((s) => ({ ...s, dirty: false }));
  };

  const handleDiscardProperty = () => {
    propertyFormData.resetFields();
    if (analysisForm) {
      propertyFormData.setFieldsValue({
        ...analysisForm,
        is_new_boleta_registral: analysisForm.is_new_boleta_registral ?? true,
      });
    }
    setPropertyStatus((s) => ({ ...s, dirty: false }));
  };

  const dispatchSave = async (values: any) => {
    await dispatch(
      onUpdateAnalysisForm({
        folder_id: activeFolder!.id,
        form_values: values,
      }),
    ).unwrap();
  };

  const onSubmitAnalysis: FormProps<any>["onFinish"] = async (values) => {
    const propertyValues = propertyFormData.getFieldsValue();
    try {
      await dispatchSave(values);
      propertyFormData.setFieldsValue(propertyValues);
      setAnalysisStatus({ dirty: false, savedAt: new Date() });
      // Refresh the local-progress snapshot with the current state of all
      // three forms (saved section's now-persisted values + any unsaved
      // drafts in the other tabs). Don't blow away the whole folder draft.
      handleSaveCurrentProgress();
      messageApi.success("Datos de análisis guardados");
    } catch (error: any) {
      messageApi.error(
        extractErrorMessage(error, "No se pudo guardar el análisis."),
      );
    }
  };

  const onSubmitRented: FormProps<any>["onFinish"] = async (values) => {
    const propertyValues = propertyFormData.getFieldsValue();
    try {
      await dispatchSave(values);
      propertyFormData.setFieldsValue(propertyValues);
      setRentedStatus({ dirty: false, savedAt: new Date() });
      handleSaveCurrentProgress();
      messageApi.success("Domicilio del inmueble arrendado guardado");
    } catch (error: any) {
      messageApi.error(
        extractErrorMessage(error, "No se pudo guardar el domicilio."),
      );
    }
  };

  const onSubmitProperty: FormProps<any>["onFinish"] = async (values) => {
    try {
      await dispatchSave(values);
      setPropertyStatus({ dirty: false, savedAt: new Date() });
      handleSaveCurrentProgress();
      messageApi.success("Datos del inmueble en garantía guardados");
    } catch (error: any) {
      messageApi.error(
        extractErrorMessage(error, "No se pudieron guardar los datos."),
      );
    }
  };

  const onChangeInput = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    name: string,
  ) => {
    const selected = e.target.value;
    if (name.includes("postal_code") && selected.length == 5) {
      dispatch(
        onSetAddressFields({
          form: analysisFormData,
          zipCode: selected,
          field: name,
        }),
      );
    }
    handleSaveCurrentProgress();
  };

  useEffect(() => {
    if (activeFolder) {
      initializeAnalysisForm();
    }
  }, [activeFolder]);

  const bodyClass = "px-5 py-4";
  const footerClass =
    "flex items-center justify-between gap-2 px-5 py-2.5 border-t border-slate-200 bg-slate-50";

  const renderFooter = (
    status: SectionStatus,
    onDiscard: () => void,
    onSave: () => void,
  ) => (
    <footer className={footerClass}>
      <StatusChip status={status} nowMs={nowMs} />
      <div className="flex items-center gap-2">
        <Button
          onClick={onDiscard}
          disabled={!status.dirty || loadingAnalysis}
        >
          Descartar
        </Button>
        <Button
          type="primary"
          icon={<SaveFilled />}
          loading={loadingAnalysis}
          disabled={!status.dirty}
          onClick={onSave}
        >
          Guardar
        </Button>
      </div>
    </footer>
  );

  const analysisTab = (
    <Form
      form={analysisFormData}
      onFinish={onSubmitAnalysis}
      onValuesChange={() => {
        setAnalysisStatus((s) => ({ ...s, dirty: true }));
        handleSaveCurrentProgress();
      }}
    >
      <div className={bodyClass}>
        <div className="flex flex-wrap -mx-2">
          {yesNoFormItems.map((formItem, key) => {
            const derivation = derivations[formItem.name];
            const currentValue = watchedAnalysisValues?.[formItem.name];
            const isAuto =
              !!derivation &&
              currentValue !== undefined &&
              currentValue !== null &&
              currentValue === derivation.value;
            const labelNode = isAuto ? (
              <span className="inline-flex items-center gap-2">
                <span>{formItem.label}</span>
                <Tooltip
                  title={`Tomado de: ${derivation.sourceLabel}. Puedes editarla. Recuerda guardar para que se persista.`}
                >
                  <span className="inline-flex items-center gap-1 text-[10px] font-medium text-blue-700 bg-blue-50 border border-blue-200 px-1.5 py-0.5 rounded">
                    <ThunderboltFilled style={{ fontSize: 9 }} />
                    Auto
                  </span>
                </Tooltip>
              </span>
            ) : (
              formItem.label
            );

            return (
              <Form.Item
                key={key}
                name={formItem.name}
                label={labelNode}
                labelCol={{ span: 24 }}
                rules={formItem.rules}
                className="w-full md:w-1/2 px-2"
              >
                <Radio.Group>
                  {formItem.options.map((option, key) => (
                    <Radio key={key} value={option.value}>
                      {option.label}
                    </Radio>
                  ))}
                </Radio.Group>
              </Form.Item>
            );
          })}
        </div>

        <div className="mt-3 pt-3 border-t border-dashed border-slate-200">
          <div className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-2 px-2">
            Datos económicos
          </div>
          <div className="flex flex-wrap -mx-2">
            {currencyFormItems.map((formItem, key) => (
              <Form.Item
                key={key}
                name={formItem.name}
                label={formItem.label}
                labelCol={{ span: 24 }}
                rules={formItem.rules}
                className="w-full md:w-1/2 px-2 !mb-2"
              >
                <Input addonBefore="$" addonAfter="mxn" />
              </Form.Item>
            ))}

            {quantityFormItems.map((formItem, key) => (
              <Form.Item
                key={key}
                name={formItem.name}
                label={formItem.label}
                labelCol={{ span: 24 }}
                rules={formItem.rules}
                className="w-full md:w-1/2 px-2 !mb-2"
              >
                <Input />
              </Form.Item>
            ))}
          </div>
        </div>
      </div>
      {renderFooter(analysisStatus, handleDiscardAnalysis, handleSaveAnalysisForm)}
    </Form>
  );

  const rentedTab = (
    <Form
      form={rentedPropertyFormData}
      onFinish={onSubmitRented}
      onValuesChange={() => {
        setRentedStatus((s) => ({ ...s, dirty: true }));
        handleSaveCurrentProgress();
      }}
    >
      <div className={bodyClass}>
        <Form.Item
          name={rentedPropertyAddressFormItem.name}
          label={rentedPropertyAddressFormItem.label}
          labelCol={{ span: 24 }}
          rules={rentedPropertyAddressFormItem.rules}
          className="w-full !mb-0"
        >
          <Input.TextArea
            rows={rentedPropertyAddressFormItem.rows}
            onChange={(e) =>
              onChangeInput(e, rentedPropertyAddressFormItem.name)
            }
            onBlur={(e) =>
              onChangeInput(e, rentedPropertyAddressFormItem.name)
            }
          />
        </Form.Item>
      </div>
      {renderFooter(rentedStatus, handleDiscardRented, handleSaveRentedPropertyForm)}
    </Form>
  );

  const propertyTab = (
    <Form
      form={propertyFormData}
      onFinish={onSubmitProperty}
      onValuesChange={() => {
        setPropertyStatus((s) => ({ ...s, dirty: true }));
        handleSaveCurrentProgress();
      }}
    >
      <div className={bodyClass}>
        <div className="flex flex-wrap -mx-2">
          {getCollateralPropertyAddressFormItems(
            isNewRegistryReceipt,
            accreditationType,
          ).map((formItem: any) => {
            if (formItem?.type === "radio") {
              return (
                <Form.Item
                  key={formItem.name}
                  name={formItem.name}
                  label={formItem.label}
                  labelCol={{ span: 24 }}
                  rules={formItem.rules}
                  className="w-full px-2"
                >
                  <Radio.Group>
                    {formItem.options.map((option: any, optionKey: number) => (
                      <Radio key={optionKey} value={option.value}>
                        {option.label}
                      </Radio>
                    ))}
                  </Radio.Group>
                </Form.Item>
              );
            } else if (formItem?.type === "input") {
              return (
                <Form.Item
                  key={formItem.name}
                  name={formItem.name}
                  label={formItem.label}
                  labelCol={{ span: 24 }}
                  rules={formItem.rules}
                  className="w-full md:w-1/2 px-2"
                >
                  <Input
                    onChange={(e) => onChangeInput(e, formItem.name)}
                    onBlur={(e) => onChangeInput(e, formItem.name)}
                  />
                </Form.Item>
              );
            } else if (formItem?.type === "textarea") {
              return (
                <Form.Item
                  key={formItem.name}
                  labelCol={{ span: 24 }}
                  className={`${formItem.className ?? ""} w-full px-2`}
                  name={formItem.name}
                  label={formItem.label}
                  rules={formItem.rules}
                  initialValue={formItem.initialValue}
                >
                  <Input.TextArea
                    rows={formItem.rows}
                    onChange={(e) => onChangeInput(e, formItem.name)}
                    onBlur={(e) => onChangeInput(e, formItem.name)}
                  />
                </Form.Item>
              );
            }
          })}
        </div>
      </div>
      {renderFooter(propertyStatus, handleDiscardProperty, handleSaveGuaranteePropertyForm)}
    </Form>
  );

  const tabItems = useMemo(() => {
    const items = [
      {
        key: "analysis",
        label: <TabLabel title="Datos de análisis" status={analysisStatus} />,
        children: analysisTab,
      },
      {
        key: "rented",
        label: (
          <TabLabel
            title="Domicilio del inmueble arrendado"
            status={rentedStatus}
          />
        ),
        children: rentedTab,
      },
    ];
    if (hasCollateralSubfolder) {
      items.push({
        key: "property",
        label: (
          <TabLabel
            title="Datos del inmueble en garantía"
            status={propertyStatus}
          />
        ),
        children: propertyTab,
      });
    }
    return items;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    analysisStatus,
    rentedStatus,
    propertyStatus,
    hasCollateralSubfolder,
    nowMs,
    loadingAnalysis,
    isNewRegistryReceipt,
    accreditationType,
    derivations,
    watchedAnalysisValues,
  ]);

  return (
    <div className="w-full max-w-5xl mx-auto p-4">
      {contextHolder}
      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
        <Tabs
          tabPosition="left"
          activeKey={activeTab}
          onChange={setActiveTab}
          items={tabItems}
          tabBarStyle={{
            paddingTop: 12,
            paddingBottom: 12,
            minWidth: 240,
            background: "#f8fafc",
            borderRight: "1px solid #e2e8f0",
          }}
          destroyInactiveTabPane={false}
        />
      </div>
    </div>
  );
};
